import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import NavComponents from "./NavComponents";

function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const navigate = useNavigate();
  
  // Add scroll effect
  useEffect(() => {
    const handleScroll = () => {
      const isScrolled = window.scrollY > 20;
      if (isScrolled !== scrolled) {
        setScrolled(isScrolled);
      }
    };
    
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [scrolled]);

  // Handle navigation to auth page
  const handleAuthNavigation = () => {
    // For demo purposes, we'll redirect to app.domain.com/auth
    // In a real app, you might use a different approach based on your setup
    window.location.href = `${window.location.protocol}//app.${window.location.host.replace('www.', '')}/auth`;
  };

  // Handle navigation to auth page with signup tab active
  const handleSignupNavigation = () => {
    // For demo purposes, we'll redirect to app.domain.com/auth?signup=true
    window.location.href = `${window.location.protocol}//app.${window.location.host.replace('www.', '')}/auth?signup=true`;
  };

  return (
    <div className="w-full sticky top-0 z-50">
      <div className="container mx-auto px-4 py-4">
        <motion.div 
          className={`${
            scrolled 
              ? "bg-white/95 shadow-lg" 
              : "bg-white/90"
          } backdrop-blur-md rounded-full w-full max-w-7xl sticky max-h-20 h-full mx-auto p-4 md:p-6 shadow-md flex items-center justify-between transition-all duration-300`}
          initial={{ y: -20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.5 }}
        >
          {/* Left Section - Logo */}
          <motion.div 
            className="flex items-center gap-2 cursor-pointer"
            whileHover={{ scale: 1.02 }}
            transition={{ type: "spring", stiffness: 400, damping: 10 }}
            onClick={() => navigate('/')}
          >
            {/* Logo SVG */}
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="34"
              height="28"
              viewBox="0 0 42 35"
              fill="none"
              className="drop-shadow-sm"
            >
              <rect
                x="0.0677338"
                y="0.27713"
                width="37.8267"
                height="30.2613"
                rx="4"
                fill="url(#paint0_linear_19_2)"
              />
              <path
                d="M8.73476 8.75372C8.73476 8.75372 15.5174 12.6015 15.5177 12.6731C15.518 12.7447 8.73476 16.4279 8.73476 16.4279"
                stroke="white"
                strokeWidth="3"
                strokeLinecap="round"
              />
              <path
                d="M22.7961 13.3357L23.1508 13.0524C25.5639 11.1248 29.0222 11.2451 31.2956 13.3357V13.3357"
                stroke="white"
                strokeWidth="3"
                strokeLinecap="round"
              />
              <path
                d="M24.4049 20.8374L28.3409 25.7922C28.3409 25.7922 27.5876 30.4375 24.866 32.605C22.1445 34.7733 12.7659 34.1534 12.7659 34.1534C12.7659 34.1534 12.9383 33.1032 13.2651 31.6379L17.5591 26.5474C17.7393 26.3341 17.5196 26.02 17.2573 26.1163L14.5162 27.1187C15.1715 25.2281 16.0011 23.4864 16.9941 22.6954C19.7156 20.5279 24.4049 20.8374 24.4049 20.8374ZM41.6528 12.4082L40.7515 11.2737C40.5024 10.9605 40.0472 10.9083 39.7345 11.1575L29.1841 19.2264L27.6348 17.2763C27.4038 16.9854 26.9477 17.0399 26.7911 17.3766L25.6416 19.8522L29.5776 24.807L32.246 24.242C32.6091 24.1653 32.7643 23.7337 32.5337 23.4433L30.9867 21.4955L41.5371 13.4266C41.8497 13.1779 41.9019 12.7218 41.6528 12.4082Z"
                fill="url(#paint1_linear_19_2)"
              />
              <defs>
                <linearGradient
                  id="paint0_linear_19_2"
                  x1="18.9811"
                  y1="0.27713"
                  x2="18.9811"
                  y2="30.5385"
                  gradientUnits="userSpaceOnUse"
                >
                  <stop stopColor="#FCD34D" />
                  <stop offset="1" stopColor="#EA580C" />
                </linearGradient>
                <linearGradient
                  id="paint1_linear_19_2"
                  x1="27.2881"
                  y1="11"
                  x2="27.2881"
                  y2="34.2354"
                  gradientUnits="userSpaceOnUse"
                >
                  <stop stopColor="#FCD34D" />
                  <stop offset="1" stopColor="#EA580C" />
                </linearGradient>
              </defs>
            </svg>

            {/* Typography */}
            <span className="font-['Inter'] font-bold tracking-wide text-lg text-[#1E293B] bg-gradient-to-r from-[#694e42] to-[#ff8c42] bg-clip-text text-transparent">
              MAILMAID
            </span>
          </motion.div>

          {/* Centered NavComponents */}
          <div className="hidden md:block absolute transform -translate-x-1/2 left-1/2">
            <NavComponents />
          </div>

          {/* Right Section - Login/Signup */}
          <div className="hidden md:flex items-center gap-3">
            <motion.button 
              className="px-4 py-2 text-[#694e42] font-medium relative group overflow-hidden cursor-pointer"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              transition={{ type: "spring", stiffness: 400, damping: 10 }}
              onClick={handleAuthNavigation}
              aria-label="Login to your account"
            >
              <span className="relative z-10">Login</span>
              <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-gradient-to-r from-[#ff8c42] to-[#694e42] group-hover:w-full transition-all duration-300"></span>
            </motion.button>

            <motion.button
              className="px-5 py-2 bg-gradient-to-r from-[#ff8c42] to-[#ff7a29] text-white rounded-full font-medium shadow-md shadow-[#ff8c42]/20 hover:shadow-lg hover:shadow-[#ff8c42]/30 transition-all duration-300 cursor-pointer"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              transition={{ type: "spring", stiffness: 400, damping: 10 }}
              onClick={handleSignupNavigation}
              aria-label="Sign up for a free account"
            >
              Get Started Free
            </motion.button>
          </div>

          {/* Mobile Menu Button */}
          <motion.button
            className="md:hidden p-2 relative z-20"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            whileTap={{ scale: 0.9 }}
            aria-label={isMenuOpen ? "Close menu" : "Open menu"}
            aria-expanded={isMenuOpen}
          >
            <div className="w-6 h-6 flex flex-col justify-center items-center">
              <span className={`block w-5 h-0.5 bg-[#694e42] transition-all duration-300 ease-out ${isMenuOpen ? 'rotate-45 translate-y-0.5' : '-translate-y-1'}`}></span>
              <span className={`block w-5 h-0.5 bg-[#694e42] transition-all duration-300 ease-out ${isMenuOpen ? 'opacity-0' : 'opacity-100'}`}></span>
              <span className={`block w-5 h-0.5 bg-[#694e42] transition-all duration-300 ease-out ${isMenuOpen ? '-rotate-45 -translate-y-0.5' : 'translate-y-1'}`}></span>
            </div>
          </motion.button>
        </motion.div>

        {/* Mobile Navigation */}
        <AnimatePresence>
          {isMenuOpen && (
            <motion.div 
              className="md:hidden pt-4 pb-2 bg-white/95 backdrop-blur-md rounded-xl mt-2 shadow-lg p-4"
              initial={{ opacity: 0, y: -10, height: 0 }}
              animate={{ opacity: 1, y: 0, height: 'auto' }}
              exit={{ opacity: 0, y: -10, height: 0 }}
              transition={{ duration: 0.3 }}
            >
              <nav className="flex flex-col gap-2">
                <motion.a
                  href="/features"
                  className="py-2 px-4 hover:bg-[#f8f9fa] rounded-md text-[#694e42] font-medium transition-all duration-200"
                  whileHover={{ x: 5 }}
                  transition={{ type: "spring", stiffness: 400, damping: 10 }}
                >
                  Features
                </motion.a>

                <motion.a
                  href="/pricing"
                  className="py-2 px-4 hover:bg-[#f8f9fa] rounded-md text-[#694e42] font-medium transition-all duration-200"
                  whileHover={{ x: 5 }}
                  transition={{ type: "spring", stiffness: 400, damping: 10 }}
                >
                  Pricing
                </motion.a>

                <motion.a
                  href="/resources"
                  className="py-2 px-4 hover:bg-[#f8f9fa] rounded-md text-[#694e42] font-medium transition-all duration-200"
                  whileHover={{ x: 5 }}
                  transition={{ type: "spring", stiffness: 400, damping: 10 }}
                >
                  Resources
                </motion.a>

                <div className="flex gap-2 mt-4">
                  <motion.button 
                    className="w-full px-4 py-2.5 text-[#694e42] border border-[#e2dad3] rounded-md font-medium hover:bg-[#f8f9fa] transition-all duration-200 cursor-pointer"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={handleAuthNavigation}
                    aria-label="Login to your account"
                  >
                    Login
                  </motion.button>
                  <motion.button 
                    className="w-full px-4 py-2.5 bg-gradient-to-r from-[#ff8c42] to-[#ff7a29] text-white rounded-md font-medium shadow-md shadow-[#ff8c42]/20 cursor-pointer"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={handleSignupNavigation}
                    aria-label="Sign up for a free account"
                  >
                    Get Started
                  </motion.button>
                </div>
              </nav>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}

export default Header;
